import {
  createContext,
  useCallback,
  useContext,
  useState,
  type PropsWithChildren,
} from "react";
import { ConfirmDialog } from "./ConfirmDialog";

type ConfirmOptions = {
  title?: string;
  confirmText?: string;
  cancelText?: string;
};

export type ConfirmFn = (message: string, options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

type PendingConfirm = {
  message: string;
  title: string;
  confirmText?: string;
  cancelText?: string;
  resolve: (value: boolean) => void;
};

/**
 * App-wide confirmation dialog — the in-system replacement for native
 * `window.confirm()`. Mounted once in AppProviders; use {@link useConfirm}
 * from any component: `if (!(await confirm("Delete this?"))) return;`
 * mirrors `window.confirm`'s call shape so existing call sites need only
 * an `await` and an import, not a rewrite.
 */
export function ConfirmProvider({ children }: PropsWithChildren) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((message, options) => {
    return new Promise<boolean>((resolve) => {
      setPending({
        message,
        title: options?.title ?? "Please confirm",
        confirmText: options?.confirmText,
        cancelText: options?.cancelText,
        resolve,
      });
    });
  }, []);

  function handleConfirm() {
    pending?.resolve(true);
    setPending(null);
  }

  function handleCancel() {
    pending?.resolve(false);
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={pending !== null}
        title={pending?.title ?? ""}
        message={pending?.message ?? ""}
        confirmText={pending?.confirmText}
        cancelText={pending?.cancelText}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmContext.Provider>
  );
}

/** In-system replacement for `window.confirm()`. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return ctx;
}
